/**
 * Con `es-ES` + MXN, `Intl` con `currencyDisplay: 'symbol'` muestra el código "MXN";
 * `narrowSymbol` devuelve "$" conservando el formato numérico del locale (p. ej. `37.141,64 $`).
 */
function currencyDisplayForCode(currencyCode: string): 'symbol' | 'narrowSymbol' {
  const c = currencyCode.toUpperCase();
  if (c === 'MXN') return 'narrowSymbol';
  return 'symbol';
}

/**
 * Con `currencyCode` (ISO desde `useCurrency()`), formatea importes con símbolo vía `Intl`.
 */
export function formatProfitabilityAmount(
  value: number | null | undefined,
  locale: string,
  currencyCode?: string
): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (currencyCode) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: currencyDisplayForCode(currencyCode),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
      return `${formatted} ${currencyCode}`;
    }
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatProfitabilityPercent(value: number | null | undefined, locale: string): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value) + ' %';
}

/** Margen neto (beneficio / ingresos asignados × 100); null si no hay ingresos. */
export function profitabilityNetMarginPercent(
  revenue: number | null | undefined,
  profit: number | null | undefined,
): number | null {
  if (revenue == null || profit == null || Number.isNaN(revenue) || Number.isNaN(profit)) return null;
  if (revenue === 0) return null;
  return (profit / revenue) * 100;
}

/** Muestra "1.234 (45,67 %)" cuando hay total > 0; si falta parte o total, "—". */
export function formatCountWithShare(
  part: number | null | undefined,
  total: number | null | undefined,
  locale: string
): string {
  if (part == null || total == null || total <= 0) return '—';
  const pct = (part / total) * 100;
  const countStr = part.toLocaleString(locale);
  const pctStr = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(pct);
  return `${countStr} (${pctStr} %)`;
}
